import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDraftResult } from '@euler/model/bi/draft';
import { BiKpiGroupViz, BiPalette } from '@euler/model/bi/viz';
import { memo, useState } from 'react';
import { View } from 'react-native';
import { BiKpiDelta, biTextEms } from './BiKpiView';
import { BiVizMismatch } from './BiVizMismatch';
import { biSeriesColor, kBiColorValues } from './functions/biColors';
import {
  biKpiGroupLines,
  kBiKpiGapPx,
  kBiKpiTileMinPx,
} from './functions/biKpiGroupLines';
import { biKpiReading } from './functions/biKpiReading';
import { biText } from './functions/biText';
import { biVizLook } from './functions/biVizLook';

/**
 * Several headline numbers side by side: each a value over its label in the
 * item's color, and how it moved when the group compares. Tiles share the
 * row while they fit, else wrap evenly (two per row on a phone).
 */
export const BiKpiGroupView = memo(
  ({
    viz,
    result,
    palette,
  }: {
    viz: BiKpiGroupViz;
    result: BiDraftResult;
    /** The dashboard's palette; a chat draft uses the default. */
    palette?: BiPalette;
  }) => {
    const { rpx } = useResponsiveContext();
    const [width, setWidth] = useState(0);
    const look = biVizLook(viz).kpi;
    const readings = viz.items.map(item =>
      biKpiReading(result, item.value, item.format, viz.compare, look),
    );
    if (readings.some(reading => reading === null)) return <BiVizMismatch />;
    const gap = rpx(kBiKpiGapPx);
    const sizes = biKpiGroupLines(
      viz.items.length,
      width,
      gap,
      rpx(kBiKpiTileMinPx),
    );
    const perRow = sizes[0] ?? 1;
    // Explicit rows of equal tiles: a row's tiles share its width by
    // flex, so no rounding can push the last one onto another line.
    const tileWidth = width ? (width - gap * (perRow - 1)) / perRow : 0;
    const items = viz.items.map((item, index) => ({ item, index }));
    const lines = sizes.map((size, row) => {
      const start = sizes.slice(0, row).reduce((sum, n) => sum + n, 0);
      return items.slice(start, start + size);
    });
    return (
      <View
        onLayout={event => setWidth(event.nativeEvent.layout.width)}
        style={{ gap }}
      >
        {width
          ? lines.map((line, row) => (
              <View key={row} style={{ flexDirection: 'row', gap }}>
                {line.map(({ item, index }) => {
                  const reading = readings[index]!;
                  const color =
                    (item.color && kBiColorValues[item.color]) ||
                    biSeriesColor(viz.style, palette, item.value, index);
                  const base = rpx(26);
                  const size = Math.max(
                    rpx(14),
                    Math.min(base, tileWidth / biTextEms(reading.text)),
                  );
                  return (
                    <View
                      key={item.value}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        alignItems: 'center',
                        gap: rpx(6),
                      }}
                    >
                      <Typography.Text
                        bold
                        size={size}
                        color={aiColors.foreground}
                        numberOfLines={1}
                      >
                        {reading.text}
                      </Typography.Text>
                      <View
                        style={{
                          paddingHorizontal: rpx(10),
                          paddingVertical: rpx(2),
                          borderRadius: rpx(10),
                          backgroundColor: color,
                          maxWidth: '100%',
                        }}
                      >
                        <Typography.Text
                          medium
                          size={rpx(12)}
                          color="#FFFFFF"
                          numberOfLines={1}
                        >
                          {item.name ?? biText(reading.ref.column.label)}
                        </Typography.Text>
                      </View>
                      {reading.previous !== undefined ? (
                        <BiKpiDelta reading={reading} caption="" />
                      ) : null}
                    </View>
                  );
                })}
                {/* A shorter row keeps its tiles the width of the others. */}
                {Array.from({ length: perRow - line.length }, (_, k) => (
                  <View key={`pad-${k}`} style={{ flex: 1 }} />
                ))}
              </View>
            ))
          : null}
      </View>
    );
  },
);
